import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, unref as _unref } from "vue"

const _hoisted_1 = { class: "max-w-screen-lg mx-auto my-6" }
const _hoisted_2 = { class: "flex flex-col w-full lg:items-start lg:justify-between lg:flex-row" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm font-medium text-neutral-600" }, [
  /*#__PURE__*/_createTextVNode(" Please fill out all the required fields with the red asterisk ("),
  /*#__PURE__*/_createElementVNode("span", { class: "text-red-600" }, "*"),
  /*#__PURE__*/_createTextVNode(") ")
], -1)
const _hoisted_4 = { class: "mt-3 text-lg font-medium lg:pl-4 lg:mt-0 md:text-2xl text-neutral-600" }
const _hoisted_5 = { class: "block font-semibold md:inline-block text-neutral-900" }
const _hoisted_6 = {
  key: 0,
  class: "grid grid-cols-6 gap-6 mt-6"
}
const _hoisted_7 = /*#__PURE__*/_createElementVNode("div", { class: "col-span-6 border-t border-gray-200" }, null, -1)
const _hoisted_8 = {
  key: 0,
  class: "col-span-full"
}
const _hoisted_9 = {
  key: 1,
  class: "col-span-6"
}
const _hoisted_10 = { class: "flex items-center col-span-6 space-x-3 justify end" }
const _hoisted_11 = { class: "flex items-center justify-end w-full space-x-3" }

import { ref, type PropType, computed, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";

import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, minLength, helpers, integer, maxLength} from '@vuelidate/validators'

import pickBy from "lodash/pickBy";

import UserLayout from "@/layouts/UserLayout.vue";
import SectionTitle from "@/components/partials/SectionTitle.vue";
import MultiSelect from "@/components/inputs/MultiSelect.vue";
import PageLoader from "@/components/loaders/PageLoader.vue";
import ButtonComponent from "@/components/buttons/ButtonComponent.vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import SuccessModal from "@/components/modals/SuccessModal.vue";
import ErrorModal from "@/components/modals/ErrorModal.vue";
import InterventionService from "@/classes/Interventions/InterventionService"
import ErrorValidation from "./Components/Classess/ErrorValidation";

import FormField from "@/views/interventions/Components/Input/FormField.vue"

import { errorResponse } from "@/helpers/errorMessage";

import { useNetworkStore } from "@/store/network";

//Interfaces
import { SelectInterface } from "@/interfaces/pages/BasePageInterface";
import { PatientInterface } from "@/interfaces/pages/InterventionForm/InterventionFormInterface";

//Classes

export default /*#__PURE__*/_defineComponent({
  __name: 'CreatePage',
  setup(__props) {

const router = useRouter();
const route = useRoute();
const networkStore = useNetworkStore();

//Route params
const patientId = ref<number | null>(route?.params?.patient);
const caseId = ref<number | null>(route?.params?.case ?? null);
const interventionType = ref<number>(route?.params?.interventionType);
const showError = ref(false);
const showErrorMessage = ref(null);

//Interfaces
const patient = ref<PatientInterface>({});

//Strings
const successMessage = ref<string>("Data has been successfully created!");

//Any
const formStructure = ref<any>({
	label: ""
});

//Number
const selectedCase = ref<number|null>(route?.params?.case ?? null);

const form = ref<any>({
	cases_id: selectedCase.value,
	interventionType: interventionType.value,
	params: [],
})

const errors =ref<any>([]);
const cases =ref<SelectInterface[]>([]);

//Booleans
const showConfirmation = ref<boolean>(false);
const showSuccess = ref<boolean>(false);
const loading = ref<boolean>(false);
const processing = ref<boolean>(false);
const isNetworkAvailable = computed<boolean>(():boolean => networkStore.isNetworkAvailable);

const init = ():void => {
	loading.value = true;
	InterventionService
		.create(patientId.value, interventionType.value, caseId.value, isNetworkAvailable.value)
		.then(({data}) => {
			patient.value = data.patient
			formStructure.value = data.form 

			if(!caseId.value){
				cases.value = data.cases
                setDefaultCaseNumber();
			}
		})
		.finally(() => {
			loading.value = false;
		});
}

const isRequired = (value:any, vm:any):boolean => {
	return vm.is_required;
}

//Validations
const rules = computed(() => {

	const rules = {
		cases_id : {
			required,
		},
		params: {
			$each: helpers.forEach({
				value: { 
					requiredIfFuction: requiredIf(isRequired),
				},
			})
		}
	}; 

	return rules;
})

const vuelidate = useVuelidate(rules, form);

const validate = async ():void => {
    showError.value = false
    showErrorMessage.value = null;
	const result = vuelidate.value.$validate();
	console.log(result);
	processing.value = false;
	loading.value = true;
	showConfirmation.value = false;
	await result
		.then(async (res) => {
			if(res) {
				await save()
			} else {
                showError.value = true;
                showErrorMessage.value = 'Please fill up all required fields';
				console.log(res);
				errors.value = ErrorValidation.getErrors(vuelidate.value)
                errorResponse(errors.value);
			}
		}).catch((err) => {
			console.log(err);
            showError.value = true;
            showErrorMessage.value = 'There was an error on the system';
		})
		.finally(() => {
			loading.value = false;
		});
}

const save = async (): void => {
	loading.value = true;
	showConfirmation.value = false;
	
	await InterventionService
		.store(
			patientId.value, 
			interventionType.value, 
			{
				cases_id: form.value.cases_id,
				interventionType: interventionType.value,
				params: pickBy(form.value.params)
			},
			isNetworkAvailable.value
		)
		.then(() => {
			showSuccess.value = true;
            resetForm();
		})
		.catch((error: object) => {
			if(error?.data?.errors){
				errors.value = errorResponse(error?.data?.errors);
			}
		})
		.finally(() => {
			loading.value = false;
		});
};

const redirectSuccess = (): void => {
	showSuccess.value = false;

	// const route = caseId?.value ? `/patient/${patientId.value}/cases/${caseId.value}/interventions` : `/intervention/${patientId.value}/view`;

	// router.replace('');
};

const getPatientAge = (value:number):void => {
	loading.value = true;
	
	if(value){
		const index = cases.value.map((item:any) => item.id).indexOf(value);
	
		patient.value.patient_age = cases.value[index].computed_age;
		patient.value.patient_birthday = cases.value[index].birthdate;
		patient.value.patient_education = cases.value[index].educational_attainment_id;
		patient.value.patient_guardian = cases.value[index].guardian;
		patient.value.patient_sex = cases.value[index].patient_sex;

		form.value.cases_id = value;
	} else {
		patient.value.patient_age = null;
		patient.value.patient_birthday = null;
		patient.value.patient_education = null;
		patient.value.patient_guardian = null;
		patient.value.patient_sex = null;
	}

	setTimeout(() => {
		loading.value = false;
	}, 300);
}

const setDefaultCaseNumber = () => {
    if (cases.value.length === 1) {
        const item = cases.value[0];
        form.value.cases_id = item.id;
    }
}

const resetForm = () => {
    form.value.params = [];
    router.replace(route.fullPath);
}

onMounted(() => {
	init()
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(UserLayout, {
    hasBack: "",
    backLink: caseId.value ? `/patient/${patientId.value}/cases/${caseId.value}/interventions` : `/intervention/${patientId.value}/view`
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(SectionTitle, {
            fontSmall: "",
            title: `${formStructure.value.label} Intervention`,
            class: "shrink-0"
          }, {
            default: _withCtx(() => [
              _hoisted_3
            ]),
            _: 1
          }, 8, ["title"]),
          _createElementVNode("p", _hoisted_4, [
            _createTextVNode("Patient's Name: "),
            _createElementVNode("span", _hoisted_5, _toDisplayString(patient.value?.name ?? ""), 1)
          ])
        ]),
        (!loading.value)
          ? (_openBlock(), _createElementBlock("form", _hoisted_6, [
              _hoisted_7,
              (errors.value?.message)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    _createElementVNode("p", {
                      class: _normalizeClass(["text-[10px] mt-1", errors.value?.message ? 'text-red-600' : 'text-gray-800']),
                      id: "email-error"
                    }, _toDisplayString(errors.value?.message), 3)
                  ]))
                : _createCommentVNode("", true),
              (!caseId.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    _createVNode(MultiSelect, {
                      options: cases.value,
                      id: "cases_id",
                      name: "case_number",
                      label: "Case Number",
                      placeholder: "Select..",
                      searchable: "",
                      required: "",
                      modelValue: form.value.cases_id,
                      "onUpdate:modelValue": [
                        _cache[0] || (_cache[0] = ($event: any) => ((form.value.cases_id) = $event)),
                        getPatientAge
                      ],
                      error: errors.value?.cases_id
                    }, null, 8, ["options", "modelValue", "error"])
                  ]))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formStructure.value?.fields, (field, index) => {
                return (_openBlock(), _createBlock(FormField, {
                  key: index,
                  field: field,
                  form: form.value.params,
                  errors: errors.value,
                  patient: patient.value
                }, null, 8, ["field", "form", "errors", "patient"]))
              }), 128)),
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("div", _hoisted_11, [
                  _createVNode(ButtonComponent, {
                    btnWidth: "quinary",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (
								_unref(router).replace(
									caseId.value
										? `/patient/${patientId.value}/cases/${caseId.value}/interventions`
										: `/intervention/${patientId.value}/view`
								)
							))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode("Cancel")
                    ]),
                    _: 1
                  }),
                  _createVNode(ButtonComponent, {
                    btnWidth: "primary",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (showConfirmation.value = true))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(" Save Record ")
                    ]),
                    _: 1
                  })
                ])
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _createVNode(ConfirmationModal, {
        typeTwo: true,
        show: showConfirmation.value,
        onCancel: _cache[3] || (_cache[3] = ($event: any) => (showConfirmation.value = false)),
        onConfirm: _cache[4] || (_cache[4] = ($event: any) => (validate())),
        "cancel-text": "Cancel",
        "action-text": "Confirm",
        title: "Save Record",
        content: "Are you sure you wish to save this record?"
      }, null, 8, ["show"]),
      _createVNode(SuccessModal, {
        show: showSuccess.value,
        onConfirm: _cache[5] || (_cache[5] = ($event: any) => (redirectSuccess())),
        "action-text": "Close",
        title: "Success!",
        content: `${formStructure.value.label} Intervention has been Created`
      }, null, 8, ["show", "content"]),
      _createVNode(ErrorModal, {
        show: showError.value,
        content: showErrorMessage.value
      }, null, 8, ["show", "content"]),
      _createVNode(PageLoader, { show: loading.value }, null, 8, ["show"]),
      _createVNode(PageLoader, { show: processing.value }, null, 8, ["show"])
    ]),
    _: 1
  }, 8, ["backLink"]))
}
}

})